import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Paper,
  Chip,
  Backdrop
} from '@mui/material';
import { Close as CloseIcon, PersonAdd as PersonAddIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import AppService from '../../Configurations/Appservice';
import axios from 'axios';
import { getAuthToken } from '../../utils/authUtils';
import { API_ENDPOINTS } from '../../Api/endpoints';
import DocumentUploadField from '../Common/DocumentUploadField';
import apiService from '../../services/api';

const GENDERS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' }
];

/**
 * QuickEmployeeAddDialog
 * A reusable dialog component for quickly adding a new employee with minimal required fields
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Callback when dialog is closed
 * @param {Function} props.onEmployeeAdded - Callback when employee is successfully added
 * @param {string} props.organizationId - Optional organization ID to used for ROOT ADMINS
 */
const QuickEmployeeAddDialog = ({ open, onClose, onEmployeeAdded, organizationId }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractionStatus, setExtractionStatus] = useState(null);
  
  const [formData, setFormData] = useState({
    firstNameEn: '',
    middleNameEn: '',
    lastNameEn: '',
    mobileNumber: '',
    dob: '',
    gender: '',
    passportNumber: '',
    passportIssue: '',
    passportExpiry: '',
    nationality: '',
    passportFile: null,
    passportPreview: null,
  });
  
  // Store extracted passport data for metadata
  const [extractedPassportData, setExtractedPassportData] = useState(null);
  
  const [errors, setErrors] = useState({});
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  
  // Handle passport file upload and extraction
  const handlePassportUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setErrors(prev => ({
        ...prev,
        passportFile: 'File exceeds the 5MB size limit'
      }));
      return;
    }
    
    // Validate file type
    const acceptedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!acceptedTypes.includes(file.type)) {
      setErrors(prev => ({
        ...prev,
        passportFile: 'Invalid file type. Please upload JPEG, PNG, or PDF'
      }));
      return;
    }
    
    // Clear previous errors
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.passportFile;
      return newErrors;
    });
    
    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    
    // Update form data with file
    setFormData(prev => ({
      ...prev,
      passportFile: file,
      passportPreview: previewUrl
    }));
    
    // Extract data from passport
    setExtracting(true);
    setExtractionStatus({ status: 'processing', message: 'Extracting data from passport...' });
    
    const extractWithRetries = async (maxRetries = 3, delay = 2000) => {
      let attempt = 0;
      while(attempt < maxRetries){
        try {
          const fd = new FormData();
          fd.append('file', file);
          fd.append('documentType', 'PASSPORT_COPY');

          const response = await apiService.api.post(
            API_ENDPOINTS.DOCUMENT_PROCESSING.EXTRACT,
            fd,
            {
              headers: {
                'Content-Type': 'multipart/form-data'
              },
              timeout: 60000
            }
          );

          return response.data;
        } catch(err) {
          attempt++;
          console.warn(`Extraction attempt ${attempt} failed:`, err);

          if(attempt < maxRetries){
            await new Promise(res => setTimeout(res, delay));
          } else {
            throw err;
          }
        }
      }
    };


    try {
      const extractedData = await extractWithRetries(3);
      console.log('Extracted passport data:', extractedData);
      
      // Store extracted data for later use when creating employee
      setExtractedPassportData(extractedData);
      
      // Auto-fill form fields
      const updates = {};
      
      // Handle full name - split into first and last name
      if (extractedData.fullName && extractedData.fullName.trim()) {
        const nameParts = extractedData.fullName.trim().split(' ');
        if (nameParts.length > 0) {
          if (!formData.firstNameEn) updates.firstNameEn = nameParts[0];
          if (!formData.lastNameEn && nameParts.length > 1) {
            updates.lastNameEn = nameParts.slice(1).join(' ');
          }
        }
      }
      
      // Handle passport number
      if (extractedData.passportNumber && !formData.passportNumber) {
        updates.passportNumber = extractedData.passportNumber;
      } else if (extractedData.documentNumber && !formData.passportNumber) {
        updates.passportNumber = extractedData.documentNumber;
      }
      
      // Handle dates - convert to YYYY-MM-DD format
      const convertDate = (dateStr) => {
        if (!dateStr) return null;
        
        // Already in YYYY-MM-DD format
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          return dateStr;
        }
        
        // MM/DD/YYYY format
        if (dateStr.includes('/')) {
          const parts = dateStr.split('/');
          if (parts.length === 3) {
            const [month, day, year] = parts;
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          }
        }
        
        // Try to parse as date
        try {
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            return date.toISOString().split('T')[0];
          }
        } catch (e) {
          console.error('Date parsing error:', e);
        }
        
        return null;
      };
      
      // Handle passport expiry
      if (extractedData.expiryDate || extractedData.passportExpiryDate) {
        const expiryDate = convertDate(extractedData.expiryDate || extractedData.passportExpiryDate);
        if (expiryDate && !formData.passportExpiry) {
          updates.passportExpiry = expiryDate;
        }
      }
      
      // Handle passport issue date
      if (extractedData.issueDate) {
        const issueDate = convertDate(extractedData.issueDate);
        if (issueDate && !formData.passportIssue) {
          updates.passportIssue = issueDate;
        }
      }
      
      // Handle date of birth
      if (extractedData.dateOfBirth) {
        const dob = convertDate(extractedData.dateOfBirth);
        if (dob && !formData.dob) {
          updates.dob = dob;
        }
      }
      
      // Handle nationality
      if (extractedData.nationality && !formData.nationality) {
        updates.nationality = extractedData.nationality;
      }

      // Handle gender
      if (extractedData.gender && !formData.gender) {
        const genderValue = extractedData.gender.toLowerCase();
        const genderOption = GENDERS.find(g => g.label.toLowerCase() === genderValue);
        if (genderOption) {
          updates.gender = genderOption.value;
        }
      }
      
      // Update form data with extracted values
      if (Object.keys(updates).length > 0) {
        setFormData(prev => ({ ...prev, ...updates }));
      }
      
      const fieldCount = Object.keys(updates).length;
      setExtractionStatus({ 
        status: 'success', 
        message: `Successfully extracted ${fieldCount} field${fieldCount !== 1 ? 's' : ''} from passport` 
      });
      
    } catch (err) {
      console.error('Error extracting passport data:', err);
      setExtractionStatus({ 
        status: 'error', 
        message: 'Failed to extract data from passport. You can still fill the form manually.' 
      });
    } finally {
      setExtracting(false);
      // Clear extraction status after 5 seconds
      setTimeout(() => {
        setExtractionStatus(null);
      }, 5000);
    }
  };
  
  // Remove passport file
  const handleRemovePassport = () => {
    if (formData.passportPreview) {
      URL.revokeObjectURL(formData.passportPreview);
    }
    setFormData(prev => ({
      ...prev,
      passportFile: null,
      passportPreview: null
    }));
    setExtractionStatus(null);
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.firstNameEn?.trim()) {
      newErrors.firstNameEn = 'First name is required';
    }
    if (!formData.lastNameEn?.trim()) {
      newErrors.lastNameEn = 'Last name is required';
    }
    if (!formData.mobileNumber?.trim()) {
      newErrors.mobileNumber = 'Mobile number is required';
    }
    if (!formData.dob) {
      newErrors.dob = 'Date of birth is required';
    }
    if (!formData.gender) {
      newErrors.gender = 'Gender is required';
    }
    if (!formData.passportNumber?.trim()) {
      newErrors.passportNumber = 'Passport number is required';
    }
    if (!formData.passportExpiry) {
      newErrors.passportExpiry = 'Passport expiry date is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async () => {
    if (!validateForm()) {
      setError('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Validate passport number and emirates ID before creating employee
      if (formData.passportNumber) {
        try {
          const validationPayload = {
            passportNumber: formData.passportNumber || null,
            emiratesId: null, // Quick add doesn't collect Emirates ID
            organizationId: organizationId
          };
          
          console.log('Validating passport number:', validationPayload);
          
          const validationResponse = await axios.post(
            API_ENDPOINTS.EMPLOYEES.VALIDATE_DOC_IDS,
            validationPayload,
            {
              headers: {
                'Authorization': `Bearer ${getAuthToken()}`
              }
            }
          );
          
          const validationResult = validationResponse.data;
          const validationErrors = {};
          
          // Check passportNumber validation
          if (validationResult.passportNumber && validationResult.passportNumber.toLowerCase() !== 'valid') {
            validationErrors.passportNumber = validationResult.passportNumber;
          }
          
          // If validation errors exist, show them and stop submission
          if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            setError(validationResult.passportNumber || 'Passport number validation failed');
            setLoading(false);
            return;
          }
          
          console.log('Passport validation passed');
        } catch (validationError) {
          console.error('Validation API error:', validationError);
          // If validation API fails, show error but allow to continue
          // (backend will handle final validation)
          console.warn('Validation API unavailable, proceeding with creation');
        }
      }
      
      // Prepare the payload to match the backend EmployeeCreationRequest format
      const payload = {
        personalInfo: {
          firstName: formData.firstNameEn,
          middleName: formData.middleNameEn || '',
          lastName: formData.lastNameEn,
          mobileNumber: formData.mobileNumber,
          dateOfBirth: formData.dob ? new Date(formData.dob).toISOString() : null,
          gender: formData.gender,
          passportNumber: formData.passportNumber,
          passportExpiry: formData.passportExpiry ? new Date(formData.passportExpiry).toISOString() : null,
          passportIssue: formData.passportIssue ? new Date(formData.passportIssue).toISOString() : null,
          nationality: formData.nationality || null,
          emiratesId: '',
          emiratesIdIssueDate: null,
          emiratesIdExpiryDate: null,
          visaNumber: '',
          visaType: '',
          visaIssueDate: null,
          visaExpiryDate: null
        },
        employmentInfo: {
          department: null,
          position: null,
          startDate: new Date().toISOString().split('T')[0],
          employeeId: '',
          status: 'ACTIVE'
        },
        contactInfo: {
          email: '',
          requiresLogin: false,
          accessLevel: 'CUSTOMER_EMPLOYEE'
        },
        uid: null,
        organizationId: organizationId
      };
      
      console.log('Submitting employee data:', payload);
      
      const response = await AppService.post('v1/employees', payload);
      
      console.log('Employee created successfully:', response);
      
      // Upload passport file after employee creation if provided
      if (formData.passportFile && response.data && response.data.uid) {
        try {
          console.log('Uploading passport file for employee:', response.data.uid);
          const uploadFd = new FormData();
          uploadFd.append('file', formData.passportFile);
          uploadFd.append('documentType', 'passport_copy');
          uploadFd.append('fileType', 'USER_DOCUMENT');
          uploadFd.append('employeeUid', response.data.uid);
          
          const uploadResponse = await apiService.api.post(
            '/v1/files/upload',
            uploadFd,
            {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            }
          );
          
          if (uploadResponse?.data?.success && uploadResponse.data.data) {
            console.log('Passport file uploaded successfully');
            
            const fileData = uploadResponse.data.data;
            let filePath = fileData.filePath;
            if (filePath && !filePath.startsWith('/')) {
              filePath = '/' + filePath;
            }
            
            // Create employee document entry via bulk API
            const documentItem = {
              documentType: 'passport_copy',
              filePath: filePath,
              fileName: fileData.originalName || formData.passportFile.name,
              contentType: fileData.contentType || formData.passportFile.type,
              fileSize: fileData.fileSize || formData.passportFile.size,
              isPrimary: true
            };
            
            console.log('Saving passport document to employee record via bulk API');
            await apiService.api.post(
              API_ENDPOINTS.EMPLOYEE_DOCUMENTS.BULK(response.data.uid),
              [documentItem]
            );
            console.log('Passport document saved successfully');
          }
        } catch (uploadError) {
          console.error('Error uploading passport file:', uploadError);
          // Don't block success, just log the error
          console.warn('Employee created but passport file upload failed');
        }
      }
      
      setSuccess(true);
      
      // Normalize employee response to match QuickEmployeeSearch structure
      // This ensures both search and quick-add return the same property names
      const createdEmployee = response.data;
      const normalizedEmployee = {
        ...createdEmployee,
        // Ensure standard field names exist (map from backend response to expected structure)
        firstNameEn: createdEmployee.firstNameEn || createdEmployee.firstName,
        middleNameEn: createdEmployee.middleNameEn || createdEmployee.middleName,
        lastNameEn: createdEmployee.lastNameEn || createdEmployee.lastName,
        nameEn: createdEmployee.nameEn || createdEmployee.fullName || 
                [createdEmployee.firstNameEn || createdEmployee.firstName, 
                 createdEmployee.middleNameEn || createdEmployee.middleName, 
                 createdEmployee.lastNameEn || createdEmployee.lastName]
                .filter(Boolean).join(' '),
        mobileNumber: createdEmployee.mobileNumber || createdEmployee.mobile || createdEmployee.phone,
        email: createdEmployee.email || '',
        dateOfBirth: createdEmployee.dateOfBirth || createdEmployee.dob,
        passportNumber: createdEmployee.passportNumber,
        passportExpiry: createdEmployee.passportExpiry || createdEmployee.passportExpiryDate,
        passportIssue: createdEmployee.passportIssue || createdEmployee.passportIssueDate,
        gender: createdEmployee.gender,
        uid: createdEmployee.uid,
        employeeId: createdEmployee.employeeId || createdEmployee.id
      };
      
      console.log('Normalized employee for callback:', normalizedEmployee);
      
      // Wait a moment to show success message
      setTimeout(() => {
        if (onEmployeeAdded) {
          onEmployeeAdded(normalizedEmployee);
        }
        handleClose();
      }, 1000);
      
    } catch (err) {
      console.error('Error creating employee:', err);
      setError(err.response?.data?.message || err.message || 'Failed to create employee');
    } finally {
      setLoading(false);
    }
  };
  
  const handleClose = () => {
    if (!loading) {
      // Reset form
      // Clean up preview URL
      if (formData.passportPreview) {
        URL.revokeObjectURL(formData.passportPreview);
      }
      
      setFormData({
        firstNameEn: '',
        middleNameEn: '',
        lastNameEn: '',
        mobileNumber: '',
        dob: '',
        gender: '',
        passportNumber: '',
        passportIssue: '',
        passportExpiry: '',
        nationality: '',
        passportFile: null,
        passportPreview: null
      });
      setErrors({});
      setError('');
      setSuccess(false);
      setExtractionStatus(null);
      setExtractedPassportData(null);
      onClose();
    }
  };
  
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
          position: 'relative'
        }
      }}
    >
      {/* Loading overlay for passport extraction */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          position: 'absolute',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          borderRadius: 2
        }}
        open={extracting}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6" color="inherit">
            Extracting passport data...
          </Typography>
          <Typography variant="body2" color="inherit">
            Please wait while we process your document
          </Typography>
        </Box>
      </Backdrop>
      
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: '1px solid #e0e0e0',
        pb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAddIcon color="primary" />
          <Typography variant="h6" component="span">
            Quick Add Employee
          </Typography>
        </Box>
        <IconButton
          edge="end"
          onClick={handleClose}
          disabled={loading}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Employee created successfully!
          </Alert>
        )}
        
        {extractionStatus && (
          <Alert 
            severity={extractionStatus.status === 'success' ? 'success' : extractionStatus.status === 'error' ? 'warning' : 'info'}
            sx={{ mb: 2 }}
          >
            {extractionStatus.message}
          </Alert>
        )}
        
        <Grid container spacing={2}>
          {/* Passport Upload Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5', border: '1px dashed #1976d2' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CloudUploadIcon color="primary" />
                <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 600 }}>
                  Upload Passport
                </Typography>
                <Chip 
                  label="Auto-fill" 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Upload passport copy to automatically extract and fill employee information
              </Typography>
              <DocumentUploadField
                documentType="passportCopy"
                label=""
                onFileSelect={handlePassportUpload}
                file={formData.passportFile}
                previewUrl={formData.passportPreview}
                isProcessing={extracting}
                processingStatus={extractionStatus}
                onRemove={handleRemovePassport}
                error={errors.passportFile}
                acceptedTypes={['image/jpeg', 'image/png', 'image/jpg', 'application/pdf']}
                showPreviewImage={true}
              />
            </Paper>
          </Grid>
          
          {/* Personal Information Section */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
              Personal Information
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="First Name (English)"
              name="firstNameEn"
              value={formData.firstNameEn}
              onChange={handleChange}
              required
              error={!!errors.firstNameEn}
              helperText={errors.firstNameEn}
              disabled={loading}
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Middle Name (English)"
              name="middleNameEn"
              value={formData.middleNameEn}
              onChange={handleChange}
              disabled={loading}
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Last Name (English)"
              name="lastNameEn"
              value={formData.lastNameEn}
              onChange={handleChange}
              required
              error={!!errors.lastNameEn}
              helperText={errors.lastNameEn}
              disabled={loading}
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Mobile Number"
              name="mobileNumber"
              value={formData.mobileNumber}
              onChange={handleChange}
              required
              error={!!errors.mobileNumber}
              helperText={errors.mobileNumber}
              disabled={loading}
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Date of Birth"
              name="dob"
              type="date"
              value={formData.dob}
              onChange={handleChange}
              required
              error={!!errors.dob}
              helperText={errors.dob}
              InputLabelProps={{ shrink: true }}
              disabled={loading}
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small" required error={!!errors.gender} disabled={loading}>
              <InputLabel>Gender</InputLabel>
              <Select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                label="Gender"
              >
                {GENDERS.map(gender => (
                  <MenuItem key={gender.value} value={gender.value}>
                    {gender.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.gender && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.gender}
                </Typography>
              )}
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Nationality"
              name="nationality"
              value={formData.nationality}
              onChange={handleChange}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Passport Number"
              name="passportNumber"
              value={formData.passportNumber}
              onChange={handleChange}
              required
              error={!!errors.passportNumber}
              helperText={errors.passportNumber}
              disabled={loading}
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Passport Issue Date"
              name="passportIssue"
              type="date"
              value={formData.passportIssue}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              disabled={loading}
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Passport Expiry Date"
              name="passportExpiry"
              type="date"
              value={formData.passportExpiry}
              onChange={handleChange}
              required
              error={!!errors.passportExpiry}
              helperText={errors.passportExpiry}
              InputLabelProps={{ shrink: true }}
              disabled={loading}
            />
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <PersonAddIcon />}
        >
          {loading ? 'Creating...' : 'Create Employee'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickEmployeeAddDialog;
